import isBoolean from 'lodash/isBoolean';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import values from 'lodash/values';
import {
  formatDateOnly,
} from 'utils/date';
import {
  getDefaultIf,
  isEmptyString,
  notEmptyString,
  setDefaultValueIf,
} from '@populateui/utils';
import {
  normalizeInsurance,
  normalizePatientName,
  normalizePhoneNumber,
} from 'pages/Dashboard/utils/helper';
import {
  CreatePatientPayload,
  PatientOtherContactInfo,
  PatientFormData,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/patient';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'utils/dayjs';
import {
  ProviderContextDTO,
} from 'dtos';

export const genders: string[] = ['Male', 'Female', 'Other'];

export function getDefaultPatientData() {
  return {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phoneNumber: '',
    otherPhoneNumber: '',
    email: '',
    genderDescription: '',
    primaryCarePhysician: {},
    hasNoPrimaryCarePhysician: false,
    otherContact: {
      contactName: '',
      email: '',
      phoneNumber: '',
      isBillingContact: false,
    },
    insurance: {
      memberNumber: '',
      groupName: '',
      groupNumber: '',
      insuranceName: '',
      insuranceOwnerFirstName: '',
      insuranceOwnerLastName: '',
      insuranceOwnerRelationshipType: '',
    },
  };
}

export function isEmptyPatientValue(prop: any, key: string) {
  return isEmptyString(prop)
    || (/phone|fax/i.test(key) && prop === '+1')
    || (isBoolean(prop) && !prop);
}

export function serializePatientForm({
  dateOfBirth,
  insurance,
  otherContact,
  phoneNumber,
  otherPhoneNumber,
  primaryCarePhysician,
  hasNoPrimaryCarePhysician,
  ...formData
}: PatientFormData): CreatePatientPayload {
  const normalizedPatientContactInfo: PatientOtherContactInfo = {
    ...otherContact,
    roles: getDefaultIf(otherContact?.roles, null, isEmpty),
    phoneNumber: normalizePhoneNumber(otherContact?.phoneNumber ?? '') ?? undefined,
    isBillingContact: otherContact?.isBillingContact ?? false,
    ...setDefaultValueIf(
      omit(otherContact, ['contactName']),
      null,
      isEmptyPatientValue,
    ),
  };

  const normalizedInsurance = !isEmptyString(insurance?.insuranceName)
    ? normalizeInsurance(insurance!) : {};

  const normalizedPrimaryCarePhysician = (!hasNoPrimaryCarePhysician && !isNil(primaryCarePhysician?.providerEntry) ? {
    providerEntry: primaryCarePhysician.providerEntry,
    firstSeenOn: notEmptyString(primaryCarePhysician?.firstSeenOn) && dayjs(primaryCarePhysician?.firstSeenOn).isValid() ? formatDateOnly(primaryCarePhysician?.firstSeenOn ?? '') : null,
    lastSeenOn: notEmptyString(primaryCarePhysician?.lastSeenOn) && dayjs(primaryCarePhysician?.lastSeenOn).isValid() ? formatDateOnly(primaryCarePhysician?.lastSeenOn ?? '') : null,
  } : null) as ProviderContextDTO;

  const normalizedFormData = {
    ...formData,
    firstName: normalizePatientName(formData.firstName),
    middleName: normalizePatientName(formData.middleName),
    lastName: normalizePatientName(formData.lastName),
  };

  return {
    ...normalizedFormData,
    ...setDefaultValueIf(normalizedFormData, null, isEmptyString),
    otherContact: values(
      omit(normalizedPatientContactInfo, ['address', 'roles', 'phoneNumber']),
    ).every(isEmptyString)
      ? null : normalizedPatientContactInfo,
    insurance: values(normalizedInsurance).every(isEmptyString)
      ? null : {
        ...normalizedInsurance,
        insuranceId: 0,
    },
    ...(!hasNoPrimaryCarePhysician ? { primaryCarePhysician: normalizedPrimaryCarePhysician } : {}),
    ...(hasNoPrimaryCarePhysician ? { hasNoPrimaryCarePhysician: true } : {}),
    dateOfBirth: formatDateOnly(dateOfBirth),
    phoneNumber: normalizePhoneNumber(phoneNumber ?? '') ?? undefined,
    ...(!isNil(otherPhoneNumber)
      ? { otherPhoneNumber: normalizePhoneNumber(otherPhoneNumber ?? '') ?? undefined }
      : {}
    ),
  };
}
